/* =========================================================
 * SCROLLBAR PERSONALIZADA
 * ========================================================= */

export function initScrollbar() {

    if (document.getElementById('custom-scrollbar-track')) {
        return;
    }

    /* ---------------------------------------------------------
     * 1. DETECÇÃO DE DISPOSITIVOS MÓVEIS
     * --------------------------------------------------------- */

    const isMobile =
        window.matchMedia('(max-width: 768px)').matches ||
        window.matchMedia('(pointer: coarse)').matches ||
        ('ontouchstart' in window) ||
        navigator.maxTouchPoints > 0;

    /*
     * Não inicializa a scrollbar customizada em dispositivos
     * mobile/tablet.
     */
    if (isMobile) {
        return;
    }


    /* ---------------------------------------------------------
     * 2. ATIVAÇÃO DA SCROLLBAR
     * --------------------------------------------------------- */

    document.documentElement.classList.add('has-custom-scrollbar');

    document.body.insertAdjacentHTML(
        'beforeend',
        `
        <div id="custom-scrollbar-track" class="custom-scrollbar-track">
            <div id="custom-scrollbar-thumb" class="custom-scrollbar-thumb"></div>
        </div>
        `
    );


    /* ---------------------------------------------------------
     * 3. ELEMENTOS
     * --------------------------------------------------------- */

    const track = document.getElementById('custom-scrollbar-track');
    const thumb = document.getElementById('custom-scrollbar-thumb');


    /* ---------------------------------------------------------
     * 4. ESTADO
     * --------------------------------------------------------- */

    let isDragging = false;
    let startY = 0;
    let startScrollTop = 0;
    let scrollTimeout = null;


    /* ---------------------------------------------------------
     * 5. ATUALIZAÇÃO DA SCROLLBAR
     * --------------------------------------------------------- */

    function updateScrollbar() {

        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = document.documentElement.clientHeight;
        const scrollTop =
            window.scrollY || document.documentElement.scrollTop;

        /*
         * Não existe conteúdo suficiente para scroll.
         */
        if (scrollHeight <= clientHeight) {
            track.style.display = 'none';
            return;
        }

        track.style.display = 'block';

        const trackHeight = track.clientHeight;

        /*
         * Calcula o tamanho proporcional do thumb.
         */
        let thumbHeight =
            (clientHeight / scrollHeight) * trackHeight;

        /*
         * Garante um tamanho mínimo para facilitar
         * a interação com o thumb.
         */
        thumbHeight = Math.max(thumbHeight, 45);

        thumb.style.height = `${thumbHeight}px`;

        /*
         * Calcula a posição do thumb.
         */
        const maxScroll = scrollHeight - clientHeight;
        const maxThumbTop = trackHeight - thumbHeight;

        const scrollProgress =
            maxScroll > 0
                ? scrollTop / maxScroll
                : 0;

        const thumbTop =
            scrollProgress * maxThumbTop;

        thumb.style.top = `${thumbTop}px`;
    }


    /* ---------------------------------------------------------
     * 6. VISIBILIDADE DA SCROLLBAR
     * --------------------------------------------------------- */

    function triggerScrollbarVisibility() {

        track.classList.add('visible');

        clearTimeout(scrollTimeout);

        if (!isDragging) {

            scrollTimeout = setTimeout(() => {

                if (!track.matches(':hover')) {
                    track.classList.remove('visible');
                }

            }, 1500);
        }
    }


    /* ---------------------------------------------------------
     * 7. EVENTO DE SCROLL
     * --------------------------------------------------------- */

    window.addEventListener('scroll', () => {

        updateScrollbar();
        triggerScrollbarVisibility();

    });


    /* ---------------------------------------------------------
     * 8. EVENTO DE RESIZE
     * --------------------------------------------------------- */

    window.addEventListener('resize', updateScrollbar);


    /* ---------------------------------------------------------
     * 9. OBSERVAÇÃO DE ALTERAÇÕES NO CONTEÚDO
     * --------------------------------------------------------- */

    const resizeObserver = new ResizeObserver(() => {
        updateScrollbar();
    });

    resizeObserver.observe(document.body);


    /* ---------------------------------------------------------
     * 10. INÍCIO DO ARRASTE
     * --------------------------------------------------------- */

    thumb.addEventListener('pointerdown', (e) => {

        isDragging = true;

        startY = e.clientY;

        startScrollTop =
            window.scrollY ||
            document.documentElement.scrollTop;

        thumb.classList.add('dragging');
        document.body.classList.add('sb-dragging');

        thumb.setPointerCapture(e.pointerId);

        e.preventDefault();
    });


    /* ---------------------------------------------------------
     * 11. MOVIMENTO DURANTE O ARRASTE
     * --------------------------------------------------------- */

    thumb.addEventListener('pointermove', (e) => {

        if (!isDragging) {
            return;
        }

        const deltaY = e.clientY - startY;

        const scrollHeight =
            document.documentElement.scrollHeight;

        const clientHeight =
            document.documentElement.clientHeight;

        const trackHeight =
            track.clientHeight;

        const thumbHeight =
            thumb.clientHeight;

        const maxScroll =
            scrollHeight - clientHeight;

        const maxThumbTop =
            trackHeight - thumbHeight;

        /*
         * Evita divisão por zero em casos extremos.
         */
        if (maxThumbTop <= 0) {
            return;
        }

        const scrollRatio =
            maxScroll / maxThumbTop;

        const targetScroll =
            startScrollTop + deltaY * scrollRatio;

        window.scrollTo(0, targetScroll);

        e.preventDefault();
    });


    /* ---------------------------------------------------------
     * 12. FINALIZAÇÃO DO ARRASTE
     * --------------------------------------------------------- */

    const stopDragging = (e) => {

        if (!isDragging) {
            return;
        }

        isDragging = false;

        thumb.classList.remove('dragging');
        document.body.classList.remove('sb-dragging');

        try {
            thumb.releasePointerCapture(e.pointerId);
        } catch (err) {
            // Pointer capture pode já ter sido liberado.
        }

        triggerScrollbarVisibility();
    };


    thumb.addEventListener('pointerup', stopDragging);
    thumb.addEventListener('pointercancel', stopDragging);


    /* ---------------------------------------------------------
     * 13. CLIQUE NO TRACK
     * --------------------------------------------------------- */

    track.addEventListener('click', (e) => {

        if (e.target === thumb) {
            return;
        }

        const clickY =
            e.clientY -
            track.getBoundingClientRect().top;

        const trackHeight =
            track.clientHeight;

        const thumbHeight =
            thumb.clientHeight;

        const scrollHeight =
            document.documentElement.scrollHeight;

        const clientHeight =
            document.documentElement.clientHeight;

        const targetThumbTop =
            clickY - thumbHeight / 2;

        const maxThumbTop =
            trackHeight - thumbHeight;

        if (maxThumbTop <= 0) {
            return;
        }

        const scrollRatio =
            (scrollHeight - clientHeight) /
            maxThumbTop;

        const targetScroll =
            targetThumbTop * scrollRatio;

        window.scrollTo({
            top: targetScroll,
            behavior: 'smooth',
        });
    });


    /* ---------------------------------------------------------
     * 14. DETECÇÃO DO MOUSE PRÓXIMO À SCROLLBAR
     * --------------------------------------------------------- */

    window.addEventListener('mousemove', (e) => {

        const distanceToRight =
            window.innerWidth - e.clientX;

        if (distanceToRight < 60) {

            track.classList.add('visible');

        } else if (
            !isDragging &&
            !track.matches(':hover')
        ) {

            triggerScrollbarVisibility();
        }
    });


    /* ---------------------------------------------------------
     * 15. ESTADO INICIAL
     * --------------------------------------------------------- */

    updateScrollbar();
}

/* =========================================================
 * FIM DA SCROLLBAR PERSONALIZADA
 * ========================================================= */